import { useCallback, useEffect, useMemo, useState } from "react";

//interfaces
import {
  ClientSettingName,
  DelayCodeControllableOptions,
  IClientSettings,
  IFlightDelay,
  IFullFlight,
} from "@vmosolutions/oc-interfaces";
import { IAircraftOptions, IAtasOptions, IDelayOptions } from "./DelaysForm";

//redux
import { useDispatch, useSelector } from "react-redux";
import { getClientSetting } from "src/redux/actions/App.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";

// form components
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { TextField } from "src/components/FormikComponents/TextField";

import { Button, FormHelperText, Grid } from "@mui/material";

// API calls
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";

import _ from "lodash";
import moment from "moment-timezone";
import { ToggleGroup } from "src/components/MUI5FormikComponents/ToggleGroup";
import { formatDelayCode } from "src/utils/delayCode";

//pass in the full array of delays and the ID of the selected delay
export interface IEditAddFlightDelayProps {
  checkedDelay?: any;
  flightId: string;
  delayOptions: IDelayOptions[];
  atasOptions: IAtasOptions[];
  aircraftOptions: IAircraftOptions[];
  windowId: string;
  airportIdent?: string[];
  fullArrayDelays: IFlightDelay[]; //always want for add and edit
  selectedDelay: IFlightDelay;
  setSelectedDelay: (delay: IFlightDelay) => void;
  actualOut: number;
  actualIn: number;
  scheduledOut: number;
  scheduledIn: number;
  departDelayDuration: number;
  arrivalDelayDurtaion: number;
  setLoadedFlight: (flightValues: IFullFlight) => void;
  updateTabState?: (dirtyStatus: boolean) => void; // update parent with dirty status.
}

export const defaultDelayValues: IFlightDelay = {
  _id: null,
  onDeparture: null,
  // start: null,
  // end: null,
  duration: null,
  delay: null,
  notes: null,
  ATA: null,
  aircraft_id: null,
  otherDesc: null,
};

function getDelayControllableKeyByValue(value: string): keyof typeof DelayCodeControllableOptions | undefined {
  return Object.entries(DelayCodeControllableOptions).find(([_, v]) => v === value)?.[0] as
    | keyof typeof DelayCodeControllableOptions
    | undefined;
}

export const EditAddFlightDelay = (props: IEditAddFlightDelayProps): JSX.Element => {
  const {
    //airportIdent,
    flightId,
    delayOptions,
    atasOptions,
    aircraftOptions,
    selectedDelay,
    setSelectedDelay,
    fullArrayDelays,
    windowId,
    actualOut,
    scheduledOut,
    setLoadedFlight,
    updateTabState,
  } = props;
  const dispatch = useDispatch();

  ////////////
  // states //
  ////////////

  //form states to keep track of
  const [departureDelay, setDepartureDelay] = useState<boolean>(true);
  const [delayReason, setDelayReason] = useState<IDelayOptions>(null);
  const [delayDuration, setDelayDuration] = useState<string>(null);
  const [note, setNote] = useState<string>("");
  const [selectedAtaCodes, setSelectedAtaCodes] = useState<IAtasOptions[]>([]);
  const [selectedAircraft, setSelectedAircraft] = useState<IAircraftOptions>(null);
  const [otherDesc, setOtherDesc] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [mosaicPane, setMosaicPane] = useState<any>(null);
  const [controllable, setControllable] = useState<DelayCodeControllableOptions | null>();
  const delayCodeFormat = useSelector((state: IRootState) => state.UISettings.delayCodeFormat);
  const delayCodeAllowControllable = useSelector((state: IRootState) => state.UISettings.delayCodeAllowControllable);

  // check to display only components for edit mode
  const [editMode, setEditMode] = useState<boolean>(false);
  const [formTouched, setFormTouched] = useState<boolean>(false);

  const [delaySetting, setDelaySetting] = useState<IClientSettings | null>();
  const [delayLoaded, setDelayLoaded] = useState<boolean>(false);

  /////////////////
  // structures  //
  /////////////////

  const user: string = useSelector((state: IRootState) => state.authentication.attributes._id);

  //need to be hear before the useEffect...
  const clearFormStates = (): void => {
    //clear all states...
    setDepartureDelay(true);
    setDelayReason(null);
    // setDelayTime({ startTime: scheduledOut, endTime: actualOut });
    setDelayDuration(null);
    setNote("");
    setSelectedAtaCodes([]);
    setSelectedAircraft(null);
    setOtherDesc("");
    setErrorMessage("");
    setControllable(null);
    //  If form is cleared, make sure selectedDelay is set back to default as well.
    setSelectedDelay(defaultDelayValues);
  };

  const durationAsMinutes: number = useMemo(() => {
    if (delayDuration) {
      const [durationHours, durationMinutes] = delayDuration.split(":").map((val: string) => parseInt(val));
      return durationHours * 60 + durationMinutes;
    }
    return 0;
  }, [delayDuration]);

  // retrive parent panel element
  useEffect(() => {
    if (windowId) setMosaicPane(document.getElementById(windowId + "-panel"));
  }, [windowId]);

  useEffect(() => {
    const getDisplaySetting = async (): Promise<void> => {
      const displaySetting = await getClientSetting(ClientSettingName.DelaySettings);
      setDelaySetting(displaySetting?.setValue?.enabled ? displaySetting : null);
      setDelayLoaded(true);
    };
    getDisplaySetting();
  }, []);

  //update if a new delay is selected or the array of delays changes
  const fillDefaultFieldValues = useCallback(
    (selectedD?: IFlightDelay) => {
      if (!!selectedD && !!selectedD._id) {
        setDepartureDelay(selectedD.onDeparture.toString() === "Departure" ? true : false); //filter serach table doesn't accept boolean values - had to convert to a string for the table
        let delay;
        if (selectedD.delay && selectedD.delay._id) {
          delay = delayOptions.find((delay: IDelayOptions) => {
            return delay._id === selectedD.delay._id && delay.delayCode._id === selectedD.delay?.delayCode?._id;
          });
          setDelayReason(delay);
        }
        const time = moment
          .utc(moment.duration(selectedD.duration as moment.DurationInputArg1, "minutes").asMilliseconds())
          .format("HH:mm");
        setDelayDuration(time);
        setNote(selectedD.notes);
        setSelectedAtaCodes(selectedD.ATA ?? []);
        setOtherDesc(selectedD.otherDesc);
        setSelectedAircraft(aircraftOptions.find((opt: IAircraftOptions) => selectedD.aircraft_id === opt.value));
        const selectedControllable = getDelayControllableKeyByValue(selectedD.delay.delayCode.controllable as string);
        if (selectedControllable) {
          setControllable(selectedControllable as DelayCodeControllableOptions);
          if (delay) updateDelayControllable(delay, selectedControllable as DelayCodeControllableOptions);
        }
      } else {
        setDepartureDelay(true);
        setDelayReason(null);
        setDelayDuration("");
        setNote("");
        setSelectedAtaCodes([]);
        setSelectedAircraft(null);
        setOtherDesc("");
        setErrorMessage("");
        setFormTouched(false);
        setControllable(null);
      }
    },
    [scheduledOut, actualOut, aircraftOptions, atasOptions, delayOptions]
  );

  //keep track of if it is an edit mode or not...
  useEffect(() => {
    if (!!selectedDelay && selectedDelay._id !== null) {
      setEditMode(true);
    } else {
      setEditMode(false);
    }
    fillDefaultFieldValues(selectedDelay);
  }, [selectedDelay]);

  //////////////
  // HANDLERS //
  //////////////

  const delayReasonFormHandler = (event: any): void => {
    setDelayReason(event);
    setControllable(null);
    //clear stored ATA codes
    setSelectedAtaCodes([]);
    setFormTouched(true);
  };

  const selectedAtaCodeFormHandler = (event: any): void => {
    setSelectedAtaCodes(event);
    setFormTouched(true);
  };

  const durationHandler = (event: any) => {
    setDelayDuration(event);
    setFormTouched(true);
  };

  const noteHandler = (event: any): void => {
    setNote(event.target.value);
    setFormTouched(true);
  };

  const otherDescHandler = (event: any): void => {
    setOtherDesc(event.target.value);
    setFormTouched(true);
  };

  const selectedAircraftHandler = (event: any): void => {
    setSelectedAircraft(event);
    setFormTouched(true);
  };

  const toggleDelayType = (): void => {
    setDepartureDelay(!departureDelay);
    setFormTouched(true);
  };

  const toggleControllable = useCallback(
    (newVal: DelayCodeControllableOptions | null) => {
      setControllable(newVal);
      if (delayReason) {
        updateDelayControllable(delayReason, newVal);
        setFormTouched(true);
      } else {
        setControllable(null);
      }
    },
    [delayReason]
  );

  const updateDelayControllable = useCallback(
    (delayReason: IDelayOptions, newVal: DelayCodeControllableOptions | null) => {
      const newDelayReason = _.cloneDeep(delayReason);
      newDelayReason.delayCode.controllable =
        DelayCodeControllableOptions[newVal as keyof typeof DelayCodeControllableOptions];
      setDelayReason(newDelayReason);
    },
    []
  );

  const saveButton = async (): Promise<void> => {
    //error check...
    const data: any = {
      onDeparture: departureDelay,
      // start: moment.utc(delayTime.startTime).toDate(),
      // end: moment.utc(delayTime.endTime).toDate(),
      duration: durationAsMinutes,
      notes: note,
      delay: delayReason,
      ATA: selectedAtaCodes,
      aircraft_id: selectedAircraft ? selectedAircraft.value : null,
      otherDesc: otherDesc,
      createdBy: user,
      modifiedBy: user,
    };
    let updatedDelays: IFlightDelay[] = null;
    if (!!editMode && !!selectedDelay && !!selectedDelay._id) {
      updatedDelays = fullArrayDelays.map((delay: IFlightDelay) => {
        if (delay._id === selectedDelay._id) {
          const edittedDelay: IFlightDelay = { ...delay, ...data };
          return edittedDelay;
        }
        return delay;
      });
    } else {
      updatedDelays = [...fullArrayDelays, data];
    }
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Updating Flight Delays...",
      successMessage: "Flight Delays Successfully Updated",
      errorMessage: "Error: Unable to Update Flight Delays",
    };
    const response = await AmplifyAPIWrapper.Put<any>(`/flights/${flightId}`, { delays: updatedDelays }, notifications);
    if (response?.newEvent?._id) {
      setFormTouched(false);
      setLoadedFlight(response?.newEvent);
      clearFormStates();
    }
  };

  // Remove delay.
  const removeDelay = async (): Promise<void> => {
    const updatedFullArrayDelays = [...fullArrayDelays];
    const deleteIndex = updatedFullArrayDelays.findIndex((delay) => delay._id === selectedDelay._id);
    if (deleteIndex > -1) {
      updatedFullArrayDelays.splice(deleteIndex, 1);
      // const response = await dispatch(updateSelectedFlight(flightId, { delays: updatedFullArrayDelays }));
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: "Removing Flight Delay...",
        successMessage: "Flight Delay Successfully Removed",
        errorMessage: "Error: Unable to Remove Flight Delay",
      };
      const response = await AmplifyAPIWrapper.Put<any>(
        `/flights/${flightId}`,
        { delays: updatedFullArrayDelays },
        notifications
      );
      if (response?.newEvent?._id) {
        setLoadedFlight(response?.newEvent);
        clearFormStates();
      }
      setFormTouched(false);
      setEditMode(false);
    }
  };

  const controllableOptions = useMemo(() => {
    return Object.keys(DelayCodeControllableOptions).map((key: string) => {
      return { label: DelayCodeControllableOptions[key as keyof typeof DelayCodeControllableOptions], value: key };
    });
  }, []);

  return (
    <Grid className="delay_form" container direction="column">
      <Grid item className="header">
        {editMode ? "Edit Selected Delay" : "Add New Delay"}
      </Grid>

      <Grid container className="required_fields">
        <Grid item className="checkbox_departure">
          <CheckboxField
            label="On Departure"
            labelPlacement="start"
            checked={departureDelay}
            onChange={toggleDelayType}
          />
        </Grid>

        <Grid item className="checkbox_arrival">
          <CheckboxField
            label="On Arrival"
            labelPlacement="start"
            checked={!departureDelay}
            onChange={toggleDelayType}
          />
        </Grid>

        {errorMessage !== "" && (
          <Grid item className="error_text">
            <FormHelperText className="error">{errorMessage}</FormHelperText>
          </Grid>
        )}

        <Grid item className="delay_code_selection">
          <SelectField
            className="modal_selection"
            name="delayReason"
            label="Delay Reason"
            value={delayReason}
            onChange={delayReasonFormHandler}
            options={delayOptions ?? []} //need to have options in the form of an array of objects {value: & label:}
            getOptionLabel={(delay: IDelayOptions) => formatDelayCode(delay, delayCodeFormat)}
            getOptionValue={(delay: IDelayOptions) => delay?.delayCode?._id}
            isSearchable={true}
            isClearable={true}
            menuPosition="fixed"
            maxMenuHeight={400}
            menuPortalTarget={mosaicPane}
            openMenuOnFocus={false}
            placeholder="Select Delay Reason"
            meta={{ error: !!delayReason || !formTouched ? null : "Delay Reason Required" }}
            filterOptions={{
              ignoreCase: true,
              ignoreAccents: true,
              matchFrom: "any" as const,
              stringify: (option: any) => `${option.label}`,
              trim: true,
            }}
          />
        </Grid>

        {delayCodeAllowControllable !== false && (
          <Grid item xs={12}>
            <ToggleGroup
              label={"Controllable"}
              options={controllableOptions}
              allowNull={true}
              name={"controllable"}
              onChange={(e, v) => {
                toggleControllable(v);
              }}
              value={controllable}
              exclusive={true}
              meta={{ error: controllable ? null : "Controllable Required" }}
              toggleGroupSX={{
                flexWrap: "wrap",
                flexDirection: "row",
                gap: "10px",
              }}
              buttonSX={{
                maxWidth: "100%",
                borderRadius: 0,
                color: "primary.main",
                border: "none",
                outline: "1px solid",
                outlineColor: "primary.main",
                fontSize: "0.75rem",
              }}
            />
          </Grid>
        )}

        {delayReason?.delayCode?.requireATA && (
          <Grid item className="ata_code_selection">
            <SelectField
              className="ATA_selection"
              name="selectedAtaCodes"
              label="ATA Codes"
              value={selectedAtaCodes}
              onChange={selectedAtaCodeFormHandler}
              getOptionLabel={(ata: IAtasOptions) =>
                ata?.ataNumber?.concat(ata?.ataCode?.code).concat("-", ata?.ataCode?.description)
              }
              getOptionValue={(ata: IAtasOptions) => ata?.ataCode?._id}
              options={atasOptions} //need to have options in the form of an array of objects {value: & label:}
              isMulti={true}
              isSearchable={true}
              isClearable={true}
              menuPosition="fixed"
              maxMenuHeight={400}
              menuPortalTarget={mosaicPane}
              openMenuOnFocus={false}
              placeholder="Select ATA code"
              meta={{ error: selectedAtaCodes?.length ? null : "ATA Code(s) Required" }}
            />
          </Grid>
        )}

        {delayLoaded && (delayReason?.delayCode?.requireOther || delaySetting?.setValue?.delayForm?.forceOtherDesc) && (
          <Grid item className="other_description_field">
            <TextField
              className="textfield"
              label={!delayLoaded ? "" : (delaySetting?.setValue?.delayForm?.descriptionLabel ?? "Other Description")}
              name="flight_delay_desc"
              value={otherDesc}
              onChange={otherDescHandler}
              placeholder=""
              multiline={true}
              minRows={2}
              maxRows={4}
              meta={{ error: !!otherDesc || !formTouched ? null : "Description Required" }}
            />
          </Grid>
        )}

        {delayReason?.delayCode?.requireTail && (
          <Grid item className="aircraft_selection">
            <SelectField
              className="tail_selection"
              name="selectedAircraft"
              label="Tail"
              value={selectedAircraft}
              onChange={selectedAircraftHandler}
              options={aircraftOptions} //need to have options in the form of an array of objects {value: & label:}
              isSearchable={true}
              isClearable={true}
              menuPosition="fixed"
              maxMenuHeight={400}
              menuPortalTarget={mosaicPane}
              openMenuOnFocus={false}
              placeholder="Select Aircraft"
              meta={{ error: selectedAircraft ? null : "Aircraft Required" }}
            />
          </Grid>
        )}
      </Grid>

      <Grid item className="duration_textfield">
        <DateTimeField
          name="Duration"
          label="Duration"
          value={delayDuration}
          onChange={durationHandler}
          isEpoch={false}
          displayOnlyTime={true}
        />
      </Grid>

      {delayLoaded && (delaySetting?.setValue?.delayForm?.hideNotes === false || !delaySetting) && (
        <Grid item className="note_field">
          <TextField
            className="textfield"
            label="Note"
            name="flight_delay_note"
            value={note}
            onChange={noteHandler}
            placeholder=""
            multiline={true}
            minRows={2}
            maxRows={4}
          />
        </Grid>
      )}

      <Grid item className="buttons">
        <Button
          id={"reset_flightdelay_btn"}
          key="clear"
          disabled={false}
          // onClick={clearFormStates}
          onClick={() => fillDefaultFieldValues(selectedDelay)}
          color="secondary"
          autoFocus={true}
        >
          Reset
        </Button>

        {editMode && (
          <Button
            id={"remove_flightdelay_btn"}
            // key="clear"
            disabled={false}
            onClick={removeDelay}
            color="secondary"
            autoFocus={true}
          >
            Remove
          </Button>
        )}

        <Button
          id={"save_flightdelay_btn"}
          className="save_button"
          key="save"
          disabled={
            !delayReason ||
            (delayReason.delayCode?.requireATA && !selectedAtaCodes?.length) ||
            (delayReason.delayCode?.requireTail && !selectedAircraft) ||
            (delayReason.delayCode?.requireOther && !otherDesc) ||
            (editMode && !formTouched) ||
            (delayCodeAllowControllable !== false && delayReason.delayCode.controllable === false)
          }
          onClick={saveButton}
          color="primary"
          autoFocus={true}
        >
          {editMode ? "Update" : "Add"}
        </Button>
      </Grid>
    </Grid>
  );
};
